import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { z } from 'zod';
import { msalInstance, redirectRequest } from '@/authSetup';
import {
  type IRedirectLoopGuard,
  redirectLoopGuard,
} from '@/lib/redirectLoopGuard';

interface RootErrorBoundaryProps {
  guard?: IRedirectLoopGuard;
}

export const RootErrorBoundary = ({
  guard = redirectLoopGuard,
}: RootErrorBoundaryProps) => {
  const error = useRouteError();
  const isUnauthorized = isRouteErrorResponse(error) && error.status === 401;
  const redirectBlocked = isUnauthorized && !guard.shouldRedirect();

  useEffect(() => {
    if (isUnauthorized && !redirectBlocked) {
      guard.registerRedirect();
      void msalInstance.loginRedirect(redirectRequest);
    }
  }, [isUnauthorized, redirectBlocked, guard]);

  if (redirectBlocked) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        Too many login attempts. Please refresh the page to try again.
      </div>
    );
  }

  if (isUnauthorized) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        Redirecting to login...
      </div>
    );
  }

  console.error(error);

  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred.';

  const items = error instanceof z.ZodError ? error.issues : [];
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center p-4">
      <h2 className="text-xl font-bold text-red-600 mb-2">
        Something went wrong
      </h2>
      <p className="text-gray-700">{message}</p>
      <ul className="list-disc list-inside text-red-500 mb-4">
        {items.map((item, index) => (
          <li key={`${item.path.join('.')}-${index}`}>{item.message}</li>
        ))}
      </ul>
    </div>
  );
};
